import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PolicyPage.css';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      <div className="policy-hero">
        <div className="container">
          <Link to="/" className="policy-back-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            Back to Home
          </Link>
          <h1 className="policy-page-title">Privacy Policy</h1>
          <p className="policy-meta">Last updated: July 2025 · iLOVEthisGAME Canada · PIPEDA Compliant</p>
        </div>
      </div>

      <div className="container">
        <div className="policy-body">

          <p>
            This Privacy Policy describes how <strong>iLOVEthisGAME</strong> (<strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>) collects,
            uses, and discloses personal information about you when you visit or make a purchase from{' '}
            <Link to="/">www.ilovethisgame.ca</Link> (the "Site"). This policy complies with Canada's{' '}
            <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong> and applicable provincial privacy legislation.
          </p>

          <h2>1. Accountability</h2>
          <p>
            iLOVEthisGAME is responsible for the personal information under its control. We have designated a Privacy
            Officer who is accountable for our compliance with PIPEDA. For any privacy-related inquiries or complaints,
            please contact us through our <Link to="/contact">Contact Page</Link>.
          </p>

          <h2>2. Identifying Purposes</h2>
          <p>We collect personal information for the following purposes, which we identify before or at the time of collection:</p>
          <ul>
            <li>Processing and fulfilling product orders (Playermaker 2.0 and accessories)</li>
            <li>Communicating with you about your order, shipping, and support</li>
            <li>Sending transactional and marketing communications (with your consent)</li>
            <li>Detecting, preventing, and investigating fraud or unauthorized activity</li>
            <li>Analyzing site usage to improve user experience and product offerings</li>
            <li>Complying with legal obligations under Canadian law</li>
          </ul>

          <h2>3. Information We Collect</h2>
          <p>We collect personal information only by fair and lawful means. The types of information we may collect include:</p>
          <ul>
            <li><strong>Order Information:</strong> Full name, billing and shipping address, email address, phone number, and payment details (processed securely via Shopify Payments and Stripe — we do not store full card numbers)</li>
            <li><strong>Device and Log Information:</strong> IP address, browser type, operating system, referring URLs, pages visited, and time zone — collected automatically when you visit the Site</li>
            <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, pixels, and similar technologies to enhance your experience and deliver relevant advertising (see Section 8 below)</li>
            <li><strong>Club or Discount Codes:</strong> Codes voluntarily entered by you during checkout — used solely to apply your discount</li>
            <li><strong>Contact Form Submissions:</strong> Name and message content submitted through our Contact page</li>
          </ul>

          <h2>4. Consent</h2>
          <p>
            We obtain your knowledge and consent before or at the time of collecting personal information, except where
            permitted or required by law. You may withdraw consent at any time, subject to legal or contractual restrictions.
            To withdraw consent or opt out of specific data uses, please contact us via our <Link to="/contact">Contact Page</Link>.
          </p>
          <p>
            By using our Site and making a purchase, you consent to the collection, use, and disclosure of your personal
            information as described in this Privacy Policy. Your consent to receive marketing communications is optional
            and can be withdrawn at any time by using the unsubscribe link in our emails.
          </p>

          <h2>5. Limiting Collection</h2>
          <p>
            We limit the collection of personal information to what is necessary for the purposes identified above.
            We do not collect personal information indiscriminately and avoid collecting information that is not needed.
          </p>

          <h2>6. How We Use Your Information</h2>
          <p>We use personal information only for the purposes for which it was collected, including:</p>
          <ul>
            <li>Fulfilling orders and processing payments</li>
            <li>Communicating about order status, shipping, and support</li>
            <li>Sending promotional emails or offers (where you have consented)</li>
            <li>Improving and optimizing our Site through analytics</li>
            <li>Screening for fraud and ensuring the security of our services</li>
            <li>Complying with applicable laws and regulations</li>
          </ul>

          <h2>7. Sharing Your Personal Information</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We may share your information
            with trusted service providers who assist us in operating our Site and fulfilling orders, strictly under
            confidentiality agreements and only for the purposes described in this policy:
          </p>
          <ul>
            <li><strong>Shopify Inc.</strong> — e-commerce platform and payment processing (servers may be located outside Canada)</li>
            <li><strong>Google LLC (Firebase, Analytics)</strong> — database hosting, site analytics, and performance monitoring</li>
            <li><strong>Meta Platforms / TikTok</strong> — advertising pixel data for delivering relevant sports technology promotions (with your consent)</li>
            <li><strong>Shipping Carriers</strong> (e.g., Canada Post, UPS) — to fulfill delivery of your order</li>
            <li><strong>Stripe Inc.</strong> — secure payment processing</li>
          </ul>
          <p>
            Some of these providers are located outside Canada. When transferring personal information internationally,
            we take reasonable steps to ensure comparable protection is in place, consistent with PIPEDA's accountability
            transfer principle.
          </p>
          <p>
            We may also disclose personal information when required by law, court order, or government authority.
          </p>

          <h2>8. Cookies &amp; Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies (pixels, local storage) to operate our Site, remember your
            preferences, and deliver targeted advertising. You may control cookie preferences through your browser settings.
            Note that disabling certain cookies may affect Site functionality.
          </p>
          <p>Types of cookies we use:</p>
          <ul>
            <li><strong>Essential cookies</strong> — required for core Site functionality (e.g., shopping cart, session management)</li>
            <li><strong>Analytics cookies</strong> — help us understand how visitors interact with the Site (Google Analytics)</li>
            <li><strong>Marketing cookies</strong> — used to serve relevant ads (Meta Pixel, TikTok Pixel) — only with your consent</li>
            <li><strong>Preference cookies</strong> — remember your choices such as dark/light theme</li>
          </ul>
          <p>You can opt out of targeted advertising:</p>
          <ul>
            <li>Facebook / Meta: <a href="https://www.facebook.com/settings/?tab=ads" target="_blank" rel="noreferrer">facebook.com/settings/?tab=ads</a></li>
            <li>Google: <a href="https://www.google.com/settings/ads/anonymous" target="_blank" rel="noreferrer">google.com/settings/ads</a></li>
          </ul>

          <h2>9. Safeguards</h2>
          <p>
            We use appropriate physical, organizational, and technical safeguards to protect your personal information
            against unauthorized access, disclosure, copying, use, or modification. These measures include HTTPS encryption,
            access controls, and secure third-party payment processing. Only authorized personnel have access to personal
            information, and only on a need-to-know basis.
          </p>

          <h2>10. Openness</h2>
          <p>
            We make information about our policies and practices for managing personal information readily available to you.
            This Privacy Policy is publicly accessible on our Site. We are committed to providing clear and transparent
            information about how we handle your data.
          </p>

          <h2>11. Your Rights Under PIPEDA</h2>
          <p>
            As a Canadian resident, you have the following rights regarding your personal information:
          </p>
          <ul>
            <li><strong>Right of Access:</strong> You may request access to personal information we hold about you</li>
            <li><strong>Right to Correction:</strong> You may request that inaccurate or incomplete information be corrected</li>
            <li><strong>Right to Withdraw Consent:</strong> You may withdraw consent to the collection, use, or disclosure of your personal information (subject to legal or contractual restrictions)</li>
            <li><strong>Right to Lodge a Complaint:</strong> You may file a complaint with us or with the Office of the Privacy Commissioner of Canada (OPC) at <a href="https://www.priv.gc.ca" target="_blank" rel="noreferrer">priv.gc.ca</a></li>
          </ul>
          <p>
            To exercise any of these rights, please contact us through our <Link to="/contact">Contact Page</Link>.
            We will respond within 30 days of receiving your request, as required by PIPEDA.
          </p>

          <h2>12. Do Not Track</h2>
          <p>
            Please note that we do not currently alter our Site's data collection and use practices in response to
            Do Not Track signals from your browser, as there is no consistent industry standard for these signals.
          </p>

          <h2>13. Data Retention</h2>
          <p>
            We retain personal information only as long as necessary to fulfill the purposes for which it was collected,
            or as required by law. Order information is retained for a minimum of 7 years for tax and legal purposes.
            You may request deletion of your personal information at any time by contacting us, subject to our legal
            obligations to retain certain records.
          </p>

          <h2>14. Minors</h2>
          <p>
            The Site is intended for youth athletes, parents, and coaches. We do not knowingly collect personal information
            from children under 13 years of age. Purchases must be authorized by a legal guardian or adult aged 18 or older.
            If you believe a child has provided us with personal information without parental consent, please contact us
            immediately.
          </p>

          <h2>15. Challenging Compliance</h2>
          <p>
            You have the right to challenge our compliance with this Privacy Policy. If you have a concern or complaint
            about how we handle your personal information, please contact our Privacy Officer through our{' '}
            <Link to="/contact">Contact Page</Link>. We will investigate and respond within 30 days.
          </p>
          <p>
            If you are not satisfied with our response, you may contact the Office of the Privacy Commissioner of Canada:
          </p>
          <ul>
            <li>Website: <a href="https://www.priv.gc.ca" target="_blank" rel="noreferrer">priv.gc.ca</a></li>
            <li>Toll-free: 1-800-282-1376</li>
          </ul>

          <h2>16. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal
            requirements, or other factors. We will post the updated policy on this page with a revised "Last Updated"
            date. We encourage you to review this policy regularly.
          </p>

          <h2>17. Contact Us</h2>
          <p>
            For any questions, access requests, corrections, or complaints regarding your personal information or our
            privacy practices, please contact us through our <Link to="/contact">Contact Us Page</Link>.
          </p>
          <p>
            We are committed to resolving privacy concerns and will respond promptly to all inquiries.
          </p>

          <div className="policy-divider" />
          <p style={{ fontSize: '1.1rem', color: 'var(--text-muted)' }}>
            iLOVEthisGAME · Official Canadian Partner of Playermaker · British Columbia, Canada · PIPEDA Compliant
          </p>
        </div>
      </div>
    </div>
  );
}
